import React, { useEffect } from "react";
import { Provider } from "react-redux";
import { Helmet } from "react-helmet";
import { ThemeProvider } from "styled-components";
import { ApolloProvider } from "@apollo/client";
import { ToastContainer } from "react-toastify";
import { PersistGate } from "redux-persist/integration/react";
import {
  VERSION,
  RECAPTCHA_CLIENT_KEY,
  SENTRY_DSN,
} from "gatsby-env-variables";
import axios from "axios";
import "react-toastify/dist/ReactToastify.css";

import { persistor, store } from "./state";
import { client } from "../graphql";
import GlobalStyle from "./styles/global-styles";
import { theme } from "./styles/theme";
import { CurrencyContextProvider } from "../components/CurrencyContext";
import { axiosErrorInterceptor } from "../components/Login/helpers/interceptors";
import { AuthComponentsProvider } from "@ifgengineering/client-auth-components";
import authSdk from "../components/Login/authSDK";
import {
  isCur8InWealthEnabled,
  isLayoutBEnabled,
  isMagicCodeEnabled,
  isTurnstileEnabled,
} from "../helpers/useFeatureFlag";
import { logException } from "../helpers/logException";
import useAnalytics from "../helpers/useAnalytics";
import {
  loginAction,
  resetPassword,
  sendResetPasswordLink,
  signupAction,
  verifyEmail,
} from "../components/Login/actions";
import { initSentry } from "../helpers/logException";
import { CUR8_SITE_URL } from "gatsby-env-variables";

const Startup = ({ element }) => {
  console.log("VERSION:", VERSION);
  initSentry(SENTRY_DSN);

  const { passwordlessVerify, passwordlessStart } = authSdk;

  useEffect(() => {
    window.location.href = CUR8_SITE_URL;
  }, []);

  useEffect(() => {
    const authInterceptor = axiosErrorInterceptor();

    axios.defaults.headers.post["x-application-id"] = "web-app";
    axios.defaults.headers.common["x-application-id"] = "web-app";

    return () => {
      axios.interceptors.request.eject(authInterceptor);
    };
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <Helmet>
          <title>IFG Wealth</title>
          <meta name="description" content="IFG Wealth" />
          <html lang="en-GB" />
          <script
            type="text/javascript"
            src="//widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js"
            async
          />
        </Helmet>
        <ApolloProvider client={client}>
          <AuthComponentsProvider
            isCur8InWealthEnabled={isCur8InWealthEnabled}
            isLayoutBEnabled={isLayoutBEnabled}
            isMagicCodeEnabled={isMagicCodeEnabled}
            isTurnstileEnabled={isTurnstileEnabled}
            logException={logException}
            recaptchaClientKey={RECAPTCHA_CLIENT_KEY}
            useAnalytics={useAnalytics}
            verifyEmail={verifyEmail}
            loginAction={loginAction}
            signupAction={signupAction}
            sendResetPasswordLink={sendResetPasswordLink}
            resetPassword={resetPassword}
            passwordlessVerify={passwordlessVerify}
            passwordlessStart={passwordlessStart}
          >
            <ThemeProvider theme={theme}>
              <CurrencyContextProvider>{element}</CurrencyContextProvider>
            </ThemeProvider>
            <ToastContainer />
          </AuthComponentsProvider>
        </ApolloProvider>
      </PersistGate>
    </Provider>
  );
};

export const wrapRootElement = ({ element }) => {
  return <Startup element={element} />;
};

export const wrapPageElement = ({ element }) => {
  return (
    <>
      <GlobalStyle />
      {element}
    </>
  );
};

export default { wrapRootElement, wrapPageElement };
